import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { uploadFile } from "@/lib/upload";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { IndianRupee, Package, Truck, RefreshCw, LogOut, FileText, Tag, Printer, Search, Plus, Trash2, Rocket, Download, Settings as SettingsIcon, RotateCcw, PlayCircle, ImagePlus, X, Loader2, Video, KeyRound, Star, MessageSquare } from "lucide-react";

const STATUSES = ["PLACED", "CONFIRMED", "READY_TO_DISPATCH", "HANDED_OVER", "LABEL_GENERATED", "PICKED_UP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED", "RTO", "RETURNED", "CANCELLED"];
const COURIERS = ["BlueDart", "Delhivery", "Xpressbees", "Ekart", "DTDC"];
const STATUS_COLOR = {
  PLACED: "bg-blue-100 text-blue-700", CONFIRMED: "bg-blue-100 text-blue-700",
  READY_TO_DISPATCH: "bg-amber-100 text-amber-700", HANDED_OVER: "bg-violet-100 text-violet-700",
  LABEL_GENERATED: "bg-violet-100 text-violet-700", PICKED_UP: "bg-amber-100 text-amber-700",
  IN_TRANSIT: "bg-amber-100 text-amber-700", OUT_FOR_DELIVERY: "bg-orange-100 text-orange-700",
  DELIVERED: "bg-emerald-100 text-emerald-700", CANCELLED: "bg-rose-100 text-rose-700",
  RETURNED: "bg-rose-100 text-rose-700", RTO: "bg-rose-100 text-rose-700",
};
const PIE_COLORS = ["#800020", "#c99700", "#733DD9", "#16A34A", "#00B29A", "#DC2626"];

function StatCard({ icon: Icon, label, value, sub, color }) {  return (
    <div className="bg-white border border-rose-900/15 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <p className="font-serif text-2xl font-bold text-rose-950 mt-3">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-xs font-medium mt-1" style={{ color }}>{sub}</p>}
    </div>
  );
}

function SizeTagInput({ value, onChange }) {
  const [draft, setDraft] = useState("");
  const addTag = (raw) => {
    const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...value];
    parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
    onChange(next);
    setDraft("");
  };
  return (
    <div className="mt-1 flex flex-wrap gap-2 items-center border border-input rounded-md px-2 py-2 min-h-[42px]" data-testid="np-sizes-tags">
      {value.map((s, i) => (
        <span key={i} className="flex items-center gap-1 bg-rose-950 text-amber-50 text-xs rounded-full pl-3 pr-1.5 py-1" data-testid={`np-size-chip-${s}`}>
          {s}
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="hover:bg-amber-50/20 rounded-full p-0.5" data-testid={`np-size-remove-${s}`}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(draft); } else if (e.key === "Backspace" && !draft && value.length) { onChange(value.slice(0, -1)); } }}
        onBlur={() => draft && addTag(draft)}
        placeholder={value.length ? "Add size + Enter" : "Type size (e.g. 32, XL, Free Size) + Enter"}
        className="flex-1 min-w-[150px] outline-none bg-transparent text-sm"
        data-testid="np-sizes-input"
      />
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, loading, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderSubTab, setOrderSubTab] = useState("pending");
  const [selIds, setSelIds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [edit, setEdit] = useState({ status: "", courier: "", tracking_id: "", awb: "", note: "" });
  const [showProduct, setShowProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const emptyProduct = { name: "", sku: "", category: "Kurtis & Tunics", price: "", mrp: "", fabric: "", sizes: ["S", "M", "L", "XL"], description: "", bullets: "", video: "", is_active: true, is_new: false, variants: [{ color: "", sku: "", images: [] }] };
  const [newProduct, setNewProduct] = useState(emptyProduct);
  const [uploading, setUploading] = useState(false);
  const [acct, setAcct] = useState({ current_password: "", new_email: "", new_password: "" });
  const [returns, setReturns] = useState([]);
  const [adminReviews, setAdminReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [repFrom, setRepFrom] = useState("");
  const [repTo, setRepTo] = useState("");
  const [repMonth, setRepMonth] = useState("");
  const [repYear, setRepYear] = useState(String(new Date().getFullYear()));

  const loadOrders = useCallback(() => {
    api.get("/orders", { params: { q: q || undefined } }).then((r) => setOrders(r.data)).catch(() => {});
  }, [q]);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
    api.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/products", { params: { all: "true" } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/returns").then((r) => setReturns(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setAdminReviews(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  useEffect(() => { if (user?.role === "admin") loadOrders(); }, [user, loadOrders]);

  if (loading || !user) return <div className="h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  const openOrder = (o) => {
    setSelected(o);
    setEdit({ status: o.status, courier: o.tracking?.courier || "", tracking_id: o.tracking?.tracking_id || "", awb: o.tracking?.awb || "", note: "" });
  };

  const saveStatus = async () => {
    try {
      await api.put(`/orders/${selected.id}/status`, edit);
      toast.success("Order updated. WhatsApp update sent to customer.");
      setSelected(null);
      loadOrders();
      api.get("/reports/summary").then((r) => setSummary(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pushShiprocket = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/orders/${selected.id}/shiprocket/create`);
      setSelected(data);
      if (data.tracking?.tracking_id) {
        toast.success(`Shiprocket synced! AWB: ${data.tracking.tracking_id} · ${data.tracking.courier || "Courier assigned"}`);
      } else {
        toast.success("Shiprocket shipment created. AWB pending.");
      }
      loadOrders();
      api.get("/reports/summary").then((r) => setSummary(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const downloadPdf = async (orderId, type) => {
    try {
      const res = await api.get(`/orders/${orderId}/${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      toast.success(`${type === "invoice" ? "Tax Invoice" : "Shipping Label"} generated`);
    } catch (e) { toast.error("Could not generate PDF"); }
  };

  const addProduct = async () => {
    try {
      const variants = (newProduct.variants || [])
        .filter((v) => v.color.trim() && v.images.length)
        .map((v) => ({ color: v.color.trim(), sku: (v.sku || "").trim(), images: v.images }));
      if (variants.length === 0) { toast.error("Add at least one color with one photo"); return; }
      const payload = {
        name: newProduct.name, sku: newProduct.sku, category: newProduct.category,
        fabric: newProduct.fabric, description: newProduct.description, video: newProduct.video,
        is_active: newProduct.is_active !== false,
        is_new: newProduct.is_new === true,
        price: parseFloat(newProduct.price), mrp: parseFloat(newProduct.mrp),
        sizes: (newProduct.sizes || []).map((s) => String(s).trim()).filter(Boolean),
        bullets: (newProduct.bullets || "").split("\n").map((s) => s.trim()).filter(Boolean),
        variants,
      };
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success("Product updated successfully!");
      } else {
        await api.post("/products", payload);
        toast.success("Product added successfully!");
      }
      setShowProduct(false);
      setEditingProduct(null);
      setNewProduct(emptyProduct);
      api.get("/products", { params: { all: "true" } }).then((r) => setProducts(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditProduct = (p) => {
    setEditingProduct(p);
    const variants = (p.variants && p.variants.length)
      ? p.variants.map((v) => ({ color: v.color, sku: v.sku || "", images: [...(v.images || [])] }))
      : [{ color: (p.colors || [])[0] || "", sku: "", images: [...(p.images || [])] }];
    setNewProduct({
      name: p.name, sku: p.sku, category: p.category, price: p.price, mrp: p.mrp,
      fabric: p.fabric || "", description: p.description || "", video: p.video || "",
      is_active: p.is_active !== false,
      is_new: p.is_new === true,
      sizes: [...(p.sizes || [])], bullets: (p.bullets || []).join("\n"), variants,
    });
    setShowProduct(true);
  };

  const setVariant = (i, key, val) => {
    const vs = [...newProduct.variants];
    vs[i] = { ...vs[i], [key]: val };
    setNewProduct({ ...newProduct, variants: vs });
  };
  const addVariant = () => setNewProduct({ ...newProduct, variants: [...newProduct.variants, { color: "", sku: "", images: [] }] });
  const removeVariant = (i) => setNewProduct({ ...newProduct, variants: newProduct.variants.filter((_, j) => j !== i) });

  const uploadVariantImages = async (i, files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); continue; }
        urls.push(await uploadFile(f));
      }
      const vs = [...newProduct.variants];
      vs[i] = { ...vs[i], images: [...vs[i].images, ...urls] };
      setNewProduct({ ...newProduct, variants: vs });
      if (urls.length) toast.success(`${urls.length} photo(s) uploaded`);
    } catch (e) { toast.error("Upload failed, please try again"); }
    finally { setUploading(false); }
  };
  const removeVariantImage = (i, idx) => {
    const vs = [...newProduct.variants];
    vs[i] = { ...vs[i], images: vs[i].images.filter((_, j) => j !== idx) };
    setNewProduct({ ...newProduct, variants: vs });
  };
  const uploadProductVideo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please select a video file"); return; }
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setNewProduct({ ...newProduct, video: url });
      toast.success("Video uploaded");
    } catch (e) { toast.error("Video upload failed (max 80MB)"); }
    finally { setUploading(false); }
  };

  const changeCreds = async () => {
    if (!acct.current_password) { toast.error("Enter your current password"); return; }
    if (!acct.new_email && !acct.new_password) { toast.error("Enter a new email or new password"); return; }
    try {
      const { data } = await api.post("/auth/admin/credentials", acct);
      loginWithToken(data.token, data.user);
      toast.success("Credentials updated successfully!");
      setAcct({ current_password: "", new_email: "", new_password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delProduct = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    setProducts((p) => p.filter((x) => x.id !== id));
  };

  const toggleReview = async (rv) => {
    try {
      await api.put(`/reviews/${rv.id}`, { approved: !(rv.approved !== false) });
      api.get("/reviews").then((r) => setAdminReviews(r.data));
      toast.success(rv.approved !== false ? "Review hidden from site" : "Review approved & visible");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      setAdminReviews((rs) => rs.filter((x) => x.id !== id));
      toast.success("Review deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteOrder = async (id, num) => {
    if (!window.confirm(`Delete order ${num}? This permanently removes it from the database and reports.`)) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted");
      loadOrders();
      api.get("/reports/summary").then((r) => setSummary(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadSlideImage = async (i, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      updateSlide(i, "image", url);
      toast.success("Slide image uploaded");
    } catch (e) { toast.error("Upload failed, please try again"); }
    finally { setUploading(false); }
  };

  const toggleSel = (id) => setSelIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const bulkPdf = async (type, status) => {
    try {
      const res = await api.get(`/orders/bulk/${type}.pdf`, { params: { status }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `all_${type}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${type === "labels" ? "All Labels" : "All Invoices"} PDF downloaded`);
    } catch (e) { toast.error("No orders to download or download failed"); }
  };

  const bulkStatus = async (ids, status, label) => {
    if (!ids.length) { toast.error("Select at least one order first"); return; }
    try {
      const { data } = await api.post("/orders/bulk/status", { order_ids: ids, status });
      let msg = `${data.updated} order(s) moved to ${label}`;
      if (data.skipped_no_tracking?.length) msg += ` · Skipped (no Tracking ID): ${data.skipped_no_tracking.join(", ")}`;
      data.skipped_no_tracking?.length ? toast.warning(msg) : toast.success(msg);
      setSelIds([]);
      loadOrders();
      api.get("/reports/summary").then((r) => setSummary(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const REPORT_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const REPORT_YEARS = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i));

  const applyMonthYear = (m, y) => {
    setRepMonth(m);
    setRepYear(y);
    if (m === "" || !y) return;
    const mi = Number(m);
    const mm = String(mi + 1).padStart(2, "0");
    const lastDay = new Date(Number(y), mi + 1, 0).getDate();
    setRepFrom(`${y}-${mm}-01`);
    setRepTo(`${y}-${mm}-${String(lastDay).padStart(2, "0")}`);
  };

  const downloadReport = async (kind, segment) => {    try {
      const params = {};
      if (repFrom) params.frm = repFrom;
      if (repTo) params.to = repTo;
      if (segment) params.segment = segment;
      const res = await api.get(`/reports/${kind}.csv`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}${segment ? "_" + segment : ""}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error("Could not download report"); }
  };

  const decideReturn = async (id, decision) => {
    const charge = decision === "APPROVED" ? parseFloat(prompt("Return/restocking charge (₹), 0 for none:", "0") || "0") : 0;
    try {
      await api.put(`/returns/${id}`, { decision, return_charge: charge });
      toast.success(`Return ${decision.toLowerCase()}`);
      api.get("/returns").then((r) => setReturns(r.data));
      loadOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Home settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateSlide = (i, key, val) => {
    const slides = [...(settings.hero_slides || [])];
    slides[i] = { ...slides[i], [key]: val };
    setSettings({ ...settings, hero_slides: slides });
  };

  const gatewayData = summary ? Object.entries(summary.by_gateway).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="min-h-screen bg-[#f7f3ee]">
      <header className="bg-rose-950 text-amber-50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <span className="font-serif text-xl font-bold">MILLION PIXELS <span className="text-amber-500 text-sm font-accent">Admin</span></span>
          <div className="flex items-center gap-3">
            <span className="text-sm hidden sm:block">{user.email}</span>
            <Button size="sm" variant="outline" className="border-amber-50/30 text-amber-50 bg-transparent hover:bg-amber-50/10" onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <Tabs defaultValue="reports">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="returns" data-testid="admin-tab-returns">Returns {returns.filter((r) => r.status === "REQUESTED").length > 0 ? `(${returns.filter((r) => r.status === "REQUESTED").length})` : ""}</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews {adminReviews.length > 0 ? `(${adminReviews.length})` : ""}</TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="downloads" data-testid="admin-tab-downloads">GST Reports</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Home Editor</TabsTrigger>
            <TabsTrigger value="account" data-testid="admin-tab-account">Account</TabsTrigger>
          </TabsList>

          {/* REPORTS */}
          <TabsContent value="reports">
            {summary && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-reports-cards">
                  <StatCard icon={IndianRupee} label="Total Revenue" value={inr(summary.revenue)} color="#800020" data-testid="admin-reports-sales-card" />
                  <StatCard icon={Package} label="Total Orders" value={summary.total_orders} sub={`${summary.pending} pending`} color="#c99700" />
                  <StatCard icon={Truck} label="Delivered" value={summary.delivered} sub={`${summary.delivery_rate}% delivery rate`} color="#16A34A" />
                  <StatCard icon={RefreshCw} label="Returns / RTO" value={summary.returns} sub={`${summary.return_rate}% return rate`} color="#DC2626" />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <StatCard icon={Tag} label="Payment Success" value={`${summary.payment_success_rate}%`} color="#733DD9" />
                  <StatCard icon={FileText} label="Prepaid Orders" value={summary.by_gateway?.razorpay || summary.by_gateway?.ONLINE || 0} color="#072654" />
                  <StatCard icon={Package} label="COD Orders" value={summary.by_gateway?.COD || 0} color="#00B29A" />
                  <StatCard icon={Truck} label="In Fulfilment" value={summary.pending} color="#EAB308" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
                  <div className="lg:col-span-2 bg-white border border-rose-900/15 rounded-xl p-5">
                    <h3 className="font-serif text-lg font-semibold text-rose-950 mb-4">Sales Trend</h3>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={summary.sales_series}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
                        <Tooltip formatter={(v) => inr(v)} />
                        <Bar dataKey="sales" fill="#800020" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="bg-white border border-rose-900/15 rounded-xl p-5">
                    <h3 className="font-serif text-lg font-semibold text-rose-950 mb-4">Payment Mode Split</h3>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={gatewayData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                          {gatewayData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                  <div className="bg-white border border-rose-900/15 rounded-xl p-5" data-testid="admin-monthly-report">
                    <h3 className="font-serif text-lg font-semibold text-rose-950 mb-4">Month-wise Sales</h3>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-muted-foreground border-b border-rose-900/10"><th className="py-2">Month</th><th>Orders</th><th className="text-right">Sales</th></tr></thead>
                      <tbody>
                        {(summary.monthly_series || []).map((m) => (
                          <tr key={m.month} className="border-b border-rose-900/5"><td className="py-2">{m.month}</td><td>{m.orders}</td><td className="text-right font-semibold">{inr(m.sales)}</td></tr>
                        ))}
                        {(!summary.monthly_series || summary.monthly_series.length === 0) && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">No data yet</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-white border border-rose-900/15 rounded-xl p-5" data-testid="admin-yearly-report">
                    <h3 className="font-serif text-lg font-semibold text-rose-950 mb-4">Year-wise Sales</h3>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-muted-foreground border-b border-rose-900/10"><th className="py-2">Year</th><th>Orders</th><th className="text-right">Sales</th></tr></thead>
                      <tbody>
                        {(summary.yearly_series || []).map((y) => (
                          <tr key={y.year} className="border-b border-rose-900/5"><td className="py-2">{y.year}</td><td>{y.orders}</td><td className="text-right font-semibold">{inr(y.sales)}</td></tr>
                        ))}
                        {(!summary.yearly_series || summary.yearly_series.length === 0) && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">No data yet</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white border border-rose-900/15 rounded-xl p-5 mt-6">
                  <h3 className="font-serif text-lg font-semibold text-rose-950 mb-4">Top Selling SKUs</h3>                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-muted-foreground border-b border-rose-900/10"><th className="py-2">SKU</th><th>Product</th><th>Units</th><th className="text-right">Revenue</th></tr></thead>
                    <tbody>
                      {summary.top_skus.map((s) => (
                        <tr key={s.sku} className="border-b border-rose-900/5"><td className="py-2 font-mono text-xs">{s.sku}</td><td>{s.name}</td><td>{s.qty}</td><td className="text-right font-semibold">{inr(s.revenue)}</td></tr>
                      ))}
                      {summary.top_skus.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No sales data yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders">
            {(() => {
              const PENDING = ["PLACED", "CONFIRMED"];
              const HANDOVER = ["HANDED_OVER", "PICKED_UP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED"];
              const pendingOrders = orders.filter((o) => PENDING.includes(o.status));
              const rtdOrders = orders.filter((o) => o.status === "READY_TO_DISPATCH");
              const handoverOrders = orders.filter((o) => HANDOVER.includes(o.status));
              const allFiltered = statusFilter === "all" ? orders : orders.filter((o) => o.status === statusFilter);
              const list = orderSubTab === "pending" ? pendingOrders : orderSubTab === "rtd" ? rtdOrders : orderSubTab === "handover" ? handoverOrders : allFiltered;
              const selectable = orderSubTab === "pending" || orderSubTab === "rtd";
              const skuText = (o) => o.items.map((i) => `${i.sku}${i.size && !String(i.sku).toUpperCase().endsWith("-" + String(i.size).toUpperCase()) ? "-" + i.size : ""} ×${i.qty}`).join(", ");
              const SUBTABS = [["pending", `Pending (${pendingOrders.length})`], ["rtd", `Ready to Dispatch (${rtdOrders.length})`], ["handover", `Handover (${handoverOrders.length})`], ["all", `All Orders (${orders.length})`]];
              return (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {SUBTABS.map(([k, label]) => (
                      <button key={k} onClick={() => { setOrderSubTab(k); setSelIds([]); }} className={`px-4 py-2 rounded-full text-sm font-accent font-medium transition-colors ${orderSubTab === k ? "bg-rose-950 text-amber-50" : "bg-rose-900/5 text-rose-950/70 hover:bg-rose-900/10"}`} data-testid={`order-subtab-${k}`}>{label}</button>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3 mb-4 items-center">
                    <form onSubmit={(e) => { e.preventDefault(); loadOrders(); }} className="flex gap-2 flex-1 min-w-[220px]">
                      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Order ID, phone, SKU, tracking..." className="h-10" data-testid="admin-order-search" />
                      <Button type="submit" variant="outline" className="h-10 border-rose-950/30"><Search className="w-4 h-4" /></Button>
                    </form>
                    {orderSubTab === "all" && (
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-48 h-10" data-testid="admin-order-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>

                  {orderSubTab === "pending" && (
                    <div className="flex flex-wrap gap-2 mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="pending-actions">
                      <Button size="sm" variant="outline" className="border-rose-950/30 h-9" onClick={() => bulkPdf("labels", "pending")} data-testid="bulk-labels-btn"><Printer className="w-4 h-4 mr-1" />All Labels PDF</Button>
                      <Button size="sm" variant="outline" className="border-rose-950/30 h-9" onClick={() => bulkPdf("invoices", "pending")} data-testid="bulk-invoices-btn"><FileText className="w-4 h-4 mr-1" />All Invoices PDF</Button>
                      <div className="flex-1" />
                      <Button size="sm" className="bg-rose-950 hover:bg-rose-900 h-9" onClick={() => bulkStatus(selIds.length ? selIds : pendingOrders.map((o) => o.id), "READY_TO_DISPATCH", "Ready to Dispatch")} data-testid="bulk-rtd-btn"><Rocket className="w-4 h-4 mr-1" />Mark Ready to Dispatch {selIds.length ? `(${selIds.length})` : "(All)"}</Button>
                    </div>
                  )}
                  {orderSubTab === "rtd" && (
                    <div className="flex flex-wrap gap-2 mb-4 bg-violet-50 border border-violet-200 rounded-lg p-3" data-testid="rtd-actions">
                      <span className="text-xs text-violet-700 self-center">Handover karne par customer ko Order ID + Tracking ID WhatsApp jayega. (Tracking ID zaroori hai)</span>
                      <div className="flex-1" />
                      <Button size="sm" className="bg-violet-700 hover:bg-violet-800 text-white h-9" onClick={() => bulkStatus(selIds.length ? selIds : rtdOrders.map((o) => o.id), "HANDED_OVER", "Handover")} data-testid="bulk-handover-btn"><Truck className="w-4 h-4 mr-1" />Mark Handover {selIds.length ? `(${selIds.length})` : "(All)"}</Button>
                    </div>
                  )}

                  <div className="bg-white border border-rose-900/15 rounded-xl overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-orders-table">
                      <thead><tr className="text-left text-muted-foreground border-b border-rose-900/10 bg-rose-900/5">
                        {selectable && <th className="py-3 px-3"><input type="checkbox" checked={list.length > 0 && selIds.length === list.length} onChange={(e) => setSelIds(e.target.checked ? list.map((o) => o.id) : [])} data-testid="order-select-all" /></th>}
                        <th className="py-3 px-4">Order ID</th><th>Customer</th><th>SKU(s)</th><th>Amount</th><th>Payment</th><th>Tracking ID</th><th>Status</th><th className="text-right px-4">Actions</th>
                      </tr></thead>
                      <tbody>
                        {list.map((o) => (
                          <tr key={o.id} className="border-b border-rose-900/5 hover:bg-rose-900/[0.02]" data-testid={`admin-order-row-${o.order_number}`}>
                            {selectable && <td className="px-3"><input type="checkbox" checked={selIds.includes(o.id)} onChange={() => toggleSel(o.id)} data-testid={`order-select-${o.order_number}`} /></td>}
                            <td className="py-3 px-4 font-mono text-xs font-semibold text-rose-950">{o.order_number}</td>
                            <td><div>{o.customer?.name}<p className="text-xs text-muted-foreground">{o.customer?.phone}</p></div></td>
                            <td className="text-xs font-mono">{skuText(o)}</td>
                            <td className="font-semibold">{inr(o.total)}</td>
                            <td className="text-xs">{o.payment_mode}</td>
                            <td className="text-xs font-mono">{o.tracking?.tracking_id || "—"}</td>
                            <td><Badge className={`${STATUS_COLOR[o.status] || "bg-gray-100"} border-0 text-[10px]`}>{o.status.replace(/_/g, " ")}</Badge></td>
                            <td className="text-right px-4">
                              <div className="flex gap-1 justify-end">
                                {orderSubTab === "rtd" && <Button size="sm" className="bg-violet-700 hover:bg-violet-800 text-white h-8" onClick={() => bulkStatus([o.id], "HANDED_OVER", "Handover")} data-testid={`admin-handover-${o.order_number}`}>Handover</Button>}
                                <Button size="sm" variant="ghost" onClick={() => downloadPdf(o.id, "label")} title="Shipping Label" data-testid={`admin-label-${o.order_number}`}><Printer className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => downloadPdf(o.id, "invoice")} title="Tax Invoice" data-testid={`admin-invoice-${o.order_number}`}><FileText className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" className="border-rose-950/30 h-8" onClick={() => openOrder(o)} data-testid={`admin-manage-${o.order_number}`}>Manage</Button>
                                <Button size="sm" variant="ghost" className="text-rose-700 h-8 px-1.5" onClick={() => deleteOrder(o.id, o.order_number)} title="Delete Order" data-testid={`admin-delete-order-${o.order_number}`}><Trash2 className="w-4 h-4" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {list.length === 0 && <tr><td colSpan={selectable ? 9 : 8} className="py-10 text-center text-muted-foreground">No orders in this tab</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-lg font-semibold text-rose-950">Catalog ({products.length})</h3>
              <Button onClick={() => setShowProduct(true)} className="bg-rose-950 hover:bg-rose-900" data-testid="admin-add-product-btn"><Plus className="w-4 h-4 mr-1" />Add Product</Button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {products.map((p) => (
                <div key={p.id} className="bg-white border border-rose-900/15 rounded-xl overflow-hidden" data-testid={`admin-product-${p.sku}`}>
                  <img src={p.images?.[0]} alt="" className="w-full aspect-[3/4] object-cover" />
                  <div className="p-3">
                    <p className="text-xs font-mono text-amber-700">{p.sku}</p>
                    <p className="text-sm font-medium leading-tight mt-1 line-clamp-1">{p.name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-semibold text-rose-950">{inr(p.price)}</span>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2 border-rose-950/20 hover:bg-rose-900/5" onClick={() => openEditProduct(p)} data-testid={`admin-edit-product-${p.sku}`}>Edit</Button>
                        <Button size="sm" variant="ghost" className="text-rose-700 h-7 px-1.5" onClick={() => delProduct(p.id)} data-testid={`admin-del-product-${p.sku}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* RETURNS */}
          <TabsContent value="returns">
            <div className="bg-white border border-rose-900/15 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-returns-table">
                <thead><tr className="text-left text-muted-foreground border-b border-rose-900/10 bg-rose-900/5">
                  <th className="py-3 px-4">Order ID</th><th>Customer</th><th>Reason</th><th>Unboxing Video</th><th>Status</th><th className="text-right px-4">Action</th>
                </tr></thead>
                <tbody>
                  {returns.map((r) => (
                    <tr key={r.id} className="border-b border-rose-900/5" data-testid={`admin-return-row-${r.order_number}`}>
                      <td className="py-3 px-4 font-mono text-xs font-semibold text-rose-950">{r.order_number}</td>
                      <td>{r.customer?.name}<p className="text-xs text-muted-foreground">{r.customer?.phone}</p></td>
                      <td className="text-xs max-w-[200px]">{r.reason}</td>
                      <td>{r.video_url ? <a href={r.video_url} target="_blank" rel="noreferrer" className="text-violet-700 flex items-center gap-1 text-xs" data-testid={`admin-return-video-${r.order_number}`}><PlayCircle className="w-4 h-4" />Watch</a> : "—"}</td>
                      <td><Badge className={`${r.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : r.status === "REJECTED" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"} border-0 text-[10px]`}>{r.status}</Badge></td>
                      <td className="text-right px-4">
                        {r.status === "REQUESTED" && (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => decideReturn(r.id, "APPROVED")} data-testid={`admin-return-approve-${r.order_number}`}>Approve</Button>
                            <Button size="sm" variant="outline" className="h-8 border-rose-700/40 text-rose-700" onClick={() => decideReturn(r.id, "REJECTED")} data-testid={`admin-return-reject-${r.order_number}`}>Reject</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {returns.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No return requests</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg font-semibold text-rose-950 flex items-center gap-2"><MessageSquare className="w-5 h-5" />Customer Reviews ({adminReviews.length})</h3>
              <p className="text-xs text-muted-foreground max-w-md text-right">Approve rehne par review site pe dikhega. Hide karne par chhup jayega (delete nahi). Delete permanent hai.</p>
            </div>
            <div className="bg-white border border-rose-900/15 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-reviews-table">
                <thead><tr className="text-left text-muted-foreground border-b border-rose-900/10 bg-rose-900/5">
                  <th className="py-3 px-4">Photo</th><th>Product</th><th>Rating</th><th>Review</th><th>Customer</th><th>Status</th><th className="text-right px-4">Action</th>
                </tr></thead>
                <tbody>
                  {adminReviews.map((rv) => (
                    <tr key={rv.id} className="border-b border-rose-900/5 align-top" data-testid={`admin-review-row-${rv.id}`}>
                      <td className="py-3 px-4">{rv.photo_url ? <a href={rv.photo_url} target="_blank" rel="noreferrer"><img src={rv.photo_url} alt="review" className="w-14 h-16 object-cover rounded-md border border-rose-900/10" /></a> : "—"}</td>
                      <td className="max-w-[160px]"><p className="font-medium leading-tight line-clamp-2">{rv.product_name}</p><p className="text-[11px] font-mono text-amber-700">{rv.product_sku}</p></td>
                      <td><span className="flex items-center gap-0.5 text-amber-600"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />{rv.rating}</span></td>
                      <td className="max-w-[240px] text-xs text-muted-foreground">{rv.text || <span className="italic">No text</span>}</td>
                      <td className="text-xs">{rv.customer_name}<p className="text-[11px] text-muted-foreground font-mono">{rv.order_number}</p></td>
                      <td><Badge className={`${rv.approved !== false ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"} border-0 text-[10px]`}>{rv.approved !== false ? "VISIBLE" : "HIDDEN"}</Badge></td>
                      <td className="text-right px-4">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" className="h-8 border-rose-950/30 text-xs" onClick={() => toggleReview(rv)} data-testid={`admin-review-toggle-${rv.id}`}>{rv.approved !== false ? "Hide" : "Approve"}</Button>
                          <Button size="sm" variant="ghost" className="text-rose-700 h-8 px-1.5" onClick={() => deleteReview(rv.id)} title="Delete Review" data-testid={`admin-review-delete-${rv.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {adminReviews.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">No reviews yet</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* GST REPORTS */}
          <TabsContent value="downloads">
            <div className="bg-white border border-rose-900/15 rounded-xl p-6 max-w-2xl">
              <h3 className="font-serif text-lg font-semibold text-rose-950 mb-1">Download Reports (GST Filing)</h3>
              <p className="text-sm text-muted-foreground mb-5">Export CSV reports for your accountant. B2B report includes customer GSTIN for input tax credit.</p>
              <div className="flex flex-wrap gap-3 mb-6 items-end">
                <div><Label className="text-xs">From</Label><Input type="date" value={repFrom} onChange={(e) => setRepFrom(e.target.value)} className="h-10 w-40" data-testid="report-from" /></div>
                <div><Label className="text-xs">To</Label><Input type="date" value={repTo} onChange={(e) => setRepTo(e.target.value)} className="h-10 w-40" data-testid="report-to" /></div>
                <div className="flex items-center gap-2 border-l border-rose-900/10 pl-3">
                  <div>
                    <Label className="text-xs">Month</Label>
                    <Select value={repMonth} onValueChange={(v) => applyMonthYear(v, repYear)}>
                      <SelectTrigger className="h-10 w-32" data-testid="report-month"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent>{REPORT_MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Year</Label>
                    <Select value={repYear} onValueChange={(v) => applyMonthYear(repMonth, v)}>
                      <SelectTrigger className="h-10 w-24" data-testid="report-year"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent>{REPORT_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mb-4 -mt-3">Month + Year select karte hi From/To automatically us month ka range set ho jayega. Phir niche se report download karein.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button variant="outline" className="justify-start border-rose-950/20 h-11" onClick={() => downloadReport("sales", "all")} data-testid="report-sales-all"><Download className="w-4 h-4 mr-2" />Sales Report (All)</Button>
                <Button variant="outline" className="justify-start border-rose-950/20 h-11" onClick={() => downloadReport("sales", "b2c")} data-testid="report-sales-b2c"><Download className="w-4 h-4 mr-2" />Sales — B2C (no GST)</Button>
                <Button variant="outline" className="justify-start border-rose-950/20 h-11" onClick={() => downloadReport("sales", "b2b")} data-testid="report-sales-b2b"><Download className="w-4 h-4 mr-2" />Sales — B2B (with GSTIN)</Button>
                <Button variant="outline" className="justify-start border-rose-950/20 h-11" onClick={() => downloadReport("returns")} data-testid="report-returns"><Download className="w-4 h-4 mr-2" />Return Report</Button>
                <Button variant="outline" className="justify-start border-rose-950/20 h-11" onClick={() => downloadReport("payments")} data-testid="report-payments"><Download className="w-4 h-4 mr-2" />Payment Report</Button>
              </div>
            </div>
          </TabsContent>

          {/* HOME EDITOR */}
          <TabsContent value="settings">
            {settings && (
              <div className="bg-white border border-rose-900/15 rounded-xl p-6 max-w-3xl space-y-5">
                <h3 className="font-serif text-lg font-semibold text-rose-950">Home Page & Store Settings</h3>
                <div><Label>Announcement Bar Text</Label><Input value={settings.announcement || ""} onChange={(e) => setSettings({ ...settings, announcement: e.target.value })} className="mt-1.5" data-testid="settings-announcement" /></div>
                <div><Label>Instagram URL</Label><Input value={settings.instagram || ""} onChange={(e) => setSettings({ ...settings, instagram: e.target.value })} className="mt-1.5" data-testid="settings-instagram" /></div>
                <div>
                  <Label>Featured Products (Home slider)</Label>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Comma se SKU daalo jo home page ke "Featured Collection" slider me dikhane hain (e.g. MP0145, MP0146)</p>
                  <Input value={(settings.featured_skus || []).join(", ")} onChange={(e) => setSettings({ ...settings, featured_skus: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="MP0145, MP0146, MP0150" data-testid="settings-featured-skus" />
                </div>
                <div>
                  <Label>Categories (add / remove)</Label>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Comma se apni categories likho. Yahi categories Shop filter aur Add Product me dikhengi. (e.g. Kurtis & Tunics, Suit Sets, Lehengas)</p>
                  <Input value={(settings.categories || []).join(", ")} onChange={(e) => setSettings({ ...settings, categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Kurtis & Tunics, Suit Sets, Anarkalis" data-testid="settings-categories" />
                </div>
                <div>
                  <Label className="mb-1 block">Hero Sliders</Label>
                  <p className="text-[11px] text-muted-foreground mb-2">Har slide home page ke top banner me 5-5 sec rotate hoga. <b>Image</b>=banner photo (upload ya URL), <b>Eyebrow</b>=chhota upar wala label, <b>Title</b>=bada heading, <b>Subtitle</b>=chhoti line, <b>Button text</b>=button pe likha, <b>Button link</b>=button kahan le jaye (poora shop: <code>/shop</code> · ek category: <code>/shop?category=Anarkalis</code> · ek specific product: <code>/product/PRODUCT-ID</code>)</p>
                  <div className="space-y-4">
                    {(settings.hero_slides || []).map((s, i) => (
                      <div key={i} className="border border-rose-900/15 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`settings-slide-${i}`}>
                      <div className="sm:col-span-2 flex items-center gap-2">
                        <Input value={s.image || ""} onChange={(e) => updateSlide(i, "image", e.target.value)} placeholder="Image URL or upload →" className="flex-1" />
                        <label className="shrink-0 flex items-center gap-1.5 text-xs border border-rose-900/20 rounded-md px-3 h-10 cursor-pointer hover:border-rose-950 transition-colors" data-testid={`slide-upload-${i}`}>
                          <ImagePlus className="w-4 h-4 text-rose-950/50" />Upload
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadSlideImage(i, e.target.files?.[0]); e.target.value = ""; }} />
                        </label>
                      </div>
                      {s.image && <img src={s.image} alt="" className="sm:col-span-2 w-full h-28 object-cover rounded-md border border-rose-900/10" />}
                      <Input value={s.eyebrow || ""} onChange={(e) => updateSlide(i, "eyebrow", e.target.value)} placeholder="Eyebrow (small label)" />
                        <Input value={s.title || ""} onChange={(e) => updateSlide(i, "title", e.target.value)} placeholder="Title" />
                        <Input value={s.subtitle || ""} onChange={(e) => updateSlide(i, "subtitle", e.target.value)} placeholder="Subtitle" />
                        <Input value={s.cta || ""} onChange={(e) => updateSlide(i, "cta", e.target.value)} placeholder="Button text" />
                        <Input value={s.link || ""} onChange={(e) => updateSlide(i, "link", e.target.value)} placeholder="Button link e.g. /shop" />
                      </div>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" className="mt-3 border-rose-950/20" onClick={() => setSettings({ ...settings, hero_slides: [...(settings.hero_slides || []), { image: "", eyebrow: "", title: "", subtitle: "", cta: "Shop Now", link: "/shop" }] })} data-testid="settings-add-slide"><Plus className="w-4 h-4 mr-1" />Add Slide</Button>
                </div>
                <Button onClick={saveSettings} className="bg-rose-950 hover:bg-rose-900" data-testid="settings-save"><SettingsIcon className="w-4 h-4 mr-2" />Save Settings</Button>
              </div>
            )}
          </TabsContent>
          {/* ACCOUNT */}
          <TabsContent value="account">
            <div className="bg-white border border-rose-900/15 rounded-xl p-6 max-w-lg">
              <h3 className="font-serif text-lg font-semibold text-rose-950 mb-1 flex items-center gap-2"><KeyRound className="w-5 h-5" />Change Login Credentials</h3>
              <p className="text-sm text-muted-foreground mb-5">Update your admin email and/or password. You will stay logged in.</p>
              <div className="space-y-4">
                <div><Label>Current Password <span className="text-rose-600">*</span></Label><Input type="password" value={acct.current_password} onChange={(e) => setAcct({ ...acct, current_password: e.target.value })} className="mt-1.5" placeholder="Enter current password to confirm" data-testid="acct-current-password" /></div>
                <div><Label>New Email (optional)</Label><Input type="email" value={acct.new_email} onChange={(e) => setAcct({ ...acct, new_email: e.target.value })} className="mt-1.5" placeholder={user.email} data-testid="acct-new-email" /></div>
                <div><Label>New Password (optional)</Label><Input type="password" value={acct.new_password} onChange={(e) => setAcct({ ...acct, new_password: e.target.value })} className="mt-1.5" placeholder="Min 6 characters" data-testid="acct-new-password" /></div>
                <Button onClick={changeCreds} className="bg-rose-950 hover:bg-rose-900" data-testid="acct-save-btn"><KeyRound className="w-4 h-4 mr-2" />Update Credentials</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Order Manage Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-order-dialog">
          <DialogHeader><DialogTitle className="font-serif text-rose-950">Manage Order {selected?.order_number}</DialogTitle><DialogDescription>Update status, courier & tracking, or push to Shiprocket.</DialogDescription></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-sm bg-rose-900/5 rounded-lg p-3">
                <p className="font-medium">{selected.customer?.name} · {selected.customer?.phone}</p>
                <p className="text-xs text-muted-foreground mt-1">{selected.shipping_address?.line1}, {selected.shipping_address?.city}, {selected.shipping_address?.state} - {selected.shipping_address?.pincode}</p>
              </div>
              <div><Label>Order Status</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="admin-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Courier</Label>
                  <Select value={edit.courier || "none"} onValueChange={(v) => setEdit({ ...edit, courier: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1.5" data-testid="admin-edit-courier"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{COURIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Tracking / AWB ID</Label><Input value={edit.tracking_id} onChange={(e) => setEdit({ ...edit, tracking_id: e.target.value })} className="mt-1.5" data-testid="admin-edit-tracking" /></div>
              </div>
              <div><Label>Note (optional)</Label><Input value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} className="mt-1.5" /></div>
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-violet-800 flex items-center gap-1.5"><Rocket className="w-4 h-4" />Shiprocket Auto-Sync</span>
                  {selected.tracking?.shipment_id && <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">Synced</Badge>}
                </div>
                {selected.tracking?.shipment_id ? (
                  <p className="text-xs text-violet-700 mt-1">Shipment #{selected.tracking.shipment_id} · AWB: {selected.tracking?.tracking_id || selected.tracking?.awb || "—"} · {selected.tracking?.courier || "Courier"}</p>
                ) : (
                  <p className="text-xs text-violet-700 mt-1">Not synced yet. One click creates the Shiprocket shipment, assigns AWB, schedules pickup & generates label.</p>
                )}
                {selected.tracking?.label_url && <a href={selected.tracking.label_url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline mt-1 inline-block" data-testid="admin-sr-label-link">Download Shiprocket Label ↗</a>}
                <Button size="sm" onClick={pushShiprocket} disabled={syncing} className="w-full mt-2 bg-[#733DD9] hover:bg-[#5d2fb5] h-9 text-white" data-testid="admin-shiprocket-push-btn">
                  {syncing ? "Syncing to Shiprocket..." : selected.tracking?.shipment_id ? "Refresh Shiprocket Sync" : "Push to Shiprocket (Auto AWB + Label)"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Updating status sends an automatic WhatsApp notification to the customer with courier & tracking details.</p>
              <Button onClick={saveStatus} className="w-full bg-rose-950 hover:bg-rose-900" data-testid="admin-save-status-btn">Save & Notify Customer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add / Edit Product Dialog */}
      <Dialog open={showProduct} onOpenChange={(v) => { setShowProduct(v); if (!v) setEditingProduct(null); }}>
        <DialogContent className="max-w-lg" data-testid="admin-product-dialog">
          <DialogHeader><DialogTitle className="font-serif text-rose-950">{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle><DialogDescription>Add colors with their own photos, sizes, bullets and an optional video.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div><Label>Name</Label><Input value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} className="mt-1" data-testid="np-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input value={newProduct.sku} onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })} className="mt-1" data-testid="np-sku" /></div>
              <div><Label>Category</Label>
                <Select value={newProduct.category} onValueChange={(v) => setNewProduct({ ...newProduct, category: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{(settings?.categories || ["Kurtis & Tunics", "Suit Sets", "Anarkalis", "Fusion Dresses"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price ₹</Label><Input type="number" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} className="mt-1" data-testid="np-price" /></div>
              <div><Label>MRP ₹</Label><Input type="number" value={newProduct.mrp} onChange={(e) => setNewProduct({ ...newProduct, mrp: e.target.value })} className="mt-1" data-testid="np-mrp" /></div>
            </div>
            <div><Label>Fabric</Label><Input value={newProduct.fabric} onChange={(e) => setNewProduct({ ...newProduct, fabric: e.target.value })} className="mt-1" /></div>
            <div><Label>Sizes</Label><SizeTagInput value={newProduct.sizes} onChange={(v) => setNewProduct({ ...newProduct, sizes: v })} /><p className="text-[11px] text-muted-foreground mt-1">Koi bhi size type karke Enter dabao — number (32, 34), alphabet (S, XL) ya "Free Size". Chip pe X se hatao.</p></div>
            <div><Label>Description</Label><Textarea value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} className="mt-1" rows={3} placeholder="Short product description" data-testid="np-description" /></div>
            <div><Label>Bullet Points (one per line)</Label><Textarea value={newProduct.bullets} onChange={(e) => setNewProduct({ ...newProduct, bullets: e.target.value })} className="mt-1" rows={3} placeholder={"Premium fabric\nMachine washable\nTrue to size"} data-testid="np-bullets" /></div>

            {/* Color variants with per-color photos */}
            <div className="border-t border-rose-900/10 pt-3">
              <Label className="flex items-center justify-between">
                <span>Colors & Photos</span>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-rose-950/20" onClick={addVariant} data-testid="np-add-color"><Plus className="w-3 h-3 mr-1" />Add Color</Button>
              </Label>
              <p className="text-[11px] text-muted-foreground mt-1">Har color ke niche uski photos upload karo. Customer color select karega to wahi photos dikhengi.</p>
              <div className="space-y-3 mt-2">
                {newProduct.variants.map((v, i) => (
                  <div key={i} className="border border-rose-900/15 rounded-lg p-3" data-testid={`np-variant-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={v.color} onChange={(e) => setVariant(i, "color", e.target.value)} placeholder="Color name (e.g. Maroon)" className="h-9" data-testid={`np-variant-color-${i}`} />
                      <Input value={v.sku || ""} onChange={(e) => setVariant(i, "sku", e.target.value)} placeholder="Color SKU (e.g. MP0145)" className="h-9" data-testid={`np-variant-sku-${i}`} />
                      {newProduct.variants.length > 1 && <Button type="button" size="sm" variant="ghost" className="text-rose-700 h-9 px-2" onClick={() => removeVariant(i)} data-testid={`np-variant-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {v.images.map((im, idx) => (
                        <div key={idx} className="relative w-16 h-20 rounded-md overflow-hidden border border-rose-900/15 group">
                          <img src={im} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removeVariantImage(i, idx)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5" data-testid={`np-variant-img-remove-${i}-${idx}`}><X className="w-3 h-3 text-white" /></button>
                        </div>
                      ))}
                      <label className="w-16 h-20 rounded-md border-2 border-dashed border-rose-900/20 flex items-center justify-center cursor-pointer hover:border-rose-950 transition-colors" data-testid={`np-variant-upload-${i}`}>
                        <ImagePlus className="w-5 h-5 text-rose-950/40" />
                        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadVariantImages(i, e.target.files); e.target.value = ""; }} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product video */}
            <div className="border-t border-rose-900/10 pt-3">
              <Label>Product Video (optional)</Label>
              {newProduct.video ? (
                <div className="flex items-center gap-2 mt-2">
                  <video src={newProduct.video} className="w-24 h-20 rounded-md object-cover bg-black" />
                  <Button type="button" size="sm" variant="outline" className="h-8 text-xs text-rose-700 border-rose-700/30" onClick={() => setNewProduct({ ...newProduct, video: "" })} data-testid="np-video-remove"><Trash2 className="w-3 h-3 mr-1" />Remove</Button>
                </div>
              ) : (
                <label className="mt-2 flex items-center gap-2 border-2 border-dashed border-rose-900/20 rounded-lg p-3 cursor-pointer hover:border-rose-950 transition-colors text-sm text-muted-foreground" data-testid="np-video-upload">
                  <Video className="w-5 h-5 text-rose-950/40" />Upload video (max 80MB)
                  <input type="file" accept="video/*" className="hidden" onChange={(e) => { uploadProductVideo(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>

            {uploading && <p className="text-xs text-amber-700 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Uploading, please wait...</p>}
            <div className="flex items-center justify-between border-t border-rose-900/10 pt-3">
              <div><Label>Show in New Arrivals</Label><p className="text-[11px] text-muted-foreground">On karne par ye product home page ke "New Arrivals" me dikhega</p></div>
              <Switch checked={newProduct.is_new === true} onCheckedChange={(v) => setNewProduct({ ...newProduct, is_new: v })} data-testid="np-new-toggle" />
            </div>
            <div className="flex items-center justify-between border-t border-rose-900/10 pt-3">
              <div><Label>Visible on website</Label><p className="text-[11px] text-muted-foreground">Off karne par product site par hide ho jayega (delete nahi hoga)</p></div>
              <Switch checked={newProduct.is_active !== false} onCheckedChange={(v) => setNewProduct({ ...newProduct, is_active: v })} data-testid="np-active-toggle" />
            </div>
            <Button onClick={addProduct} disabled={uploading} className="w-full bg-rose-950 hover:bg-rose-900" data-testid="np-save">{editingProduct ? "Update Product" : "Add Product"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
